/* ***************************************************************** */
/* TODO: eslint pre-existing exceptions auto generated on 2022-09-08 */
/*       Do not add to these rule exceptions. Only remove as the     */
/*       violations are cleaned from our code.                       */
/*                                                                   */
/*       eslint-disable array-callback-return                        */
/*       eslint-disable radix                                        */
/*       eslint-disable no-restricted-globals                        */
/* ***************************************************************** */
/* TODO: fix this 👇 */
/* eslint-disable react-hooks/exhaustive-deps */

import React, { useState, useEffect } from 'react';
import { Tabs, Tab, Box, TextField } from '@mui/material';
import { VegaLite } from 'react-vega';
import GroupAdjustment from './GroupAdjustment';
import { getFormattedStats } from '../../lib/charts';
import {
  getNProtectantStats,
  getNProtectantStatsPreview,
  getNProtectantFullFieldAdjustment,
  getNProtectantGroupAdjustments,
  setNProtectantFullFieldAdjustment,
  setNProtectantGroupAdjustments,
} from '../../lib/api';
import ApexChart from '../../components/Chart';
import DraggableModal from '../../components/DraggableModal';
import { getRounded } from '../../lib/numbers';
import useDebounce from '../../hooks/useDebounce';
import { BASE_RATE_MAPPING, MAX_ADJUSTMENT_FACTOR } from './constants';
import { getChartColors } from '../../services/chart-colors';
import { usePermission } from '../../hooks/use-permission';
import { useMapRefresh } from '../../components/google/maps/use-map-refresh';

export default function FieldDetails({ field, handleClose, reloadFields }) {
  const canAdjust = usePermission('RUN_RX');
  const [tabs, setTabs] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [allStats, setAllStats] = useState([]);
  const [colors, setColors] = useState([]);
  const [adjustments, setAdjustments] = useState({});
  const debouncedAdjustments = useDebounce(adjustments, 500);
  const { refresh: refreshMap } = useMapRefresh();

  useEffect(() => {
    if (!allStats) return;
    if (!debouncedAdjustments || debouncedAdjustments.length === 0) return;

    const fixedAdjustments = {};

    allStats.forEach((stats) => {
      if (!debouncedAdjustments[stats.key]) return;

      fixedAdjustments[stats.key] = {
        ...debouncedAdjustments[stats.key],

        groupAdjustments: debouncedAdjustments[stats.key].groupAdjustments.map(
          (ga) => ({
            ...ga,
            adjustmentFactor: enforceBounds(
              stats.key,
              stats.product,
              ga.adjustmentFactor,
              ga.minValue,
            ),
          }),
        ),
      };
    });

    getNProtectantStatsPreview(field.aaManagedId, fixedAdjustments).then(
      (stats) => {
        setAllStats(stats);
        setColors(
          allStats.map(({ product, key }) =>
            getChartColors(`n-protectant-${product}-${key}`),
          ),
        );
      },
    );
  }, [field, debouncedAdjustments]);

  useEffect(() => {
    getNProtectantStats(field.aaManagedId).then(async (rawStats) => {
      setAllStats(rawStats);
      const adj = {};
      await Promise.all(
        rawStats.map((stats) => {
          Promise.all([
            getNProtectantGroupAdjustments(
              field.aaManagedId,
              stats.key,
              stats.product,
            ),
            getNProtectantFullFieldAdjustment(
              field.aaManagedId,
              stats.key,
              stats.product,
            ),
          ]).then(([groupAdjustments, fullFieldAdjustment]) => {
            if (groupAdjustments.length > 0 && fullFieldAdjustment.length > 0) {
              adj[stats.key] = {
                fullFieldAdjustment: fullFieldAdjustment[0].adjustment,
                groupAdjustments: groupAdjustments.map((a) => ({
                  minValue: a.minValue,
                  maxValue: a.maxValue,
                  adjustmentFactor: parseInt(
                    isNaN(a.adjustment) ? 0 : a.adjustment,
                  ),
                })),
              };
            }
          });
        }),
      );

      setAdjustments(adj);
      setTabs(
        rawStats
          .map(({ key, label }) => ({ key, label }))
          .filter(({ key }) => field.stats[key]),
      );
    });
  }, [field]);

  useEffect(() => {
    Promise.all(
      allStats.map((stats) =>
        getChartColors(
          `n-protectant-${stats.product}-${stats.key}`,
          stats.distribution.map(({ label }) => label),
        ),
      ),
    ).then(setColors);
  }, [allStats]);

  return (
    <DraggableModal
      handleClose={handleClose}
      isLoading={!tabs}
      title={field.fieldName}
    >
      <div style={{ width: '860px' }}>{buildModalContent()}</div>
    </DraggableModal>
  );

  function onTabChange(_, index) {
    setCurrentTab(index);
  }

  function buildTabHeader(tab) {
    return <Tab label={tab.label} key={tab.key} />;
  }

  function buildTabContent(tab) {
    const targetStats = allStats[currentTab];
    if (targetStats.key !== tab.key || !colors[currentTab]) return null;
    return buildChartsFor(targetStats);
  }

  function buildChartsFor(stats) {
    const formattedStats = getFormattedStats(stats, colors[currentTab]);
    const domain = formattedStats.chart.values.map((cv) => cv.label);
    const range = formattedStats.colors.map((c) => rgbaToHex(c));
    const mapSpec = {
      height: 200,
      width: 200,
      padding: 5,
      mark: { type: 'geoshape' },
      projection: { type: 'mercator' },
      data: {
        values: { type: 'FeatureCollection', features: stats.features },
        format: { type: 'json', property: 'features' },
      },
      encoding: {
        color: {
          type: 'quantitative',
          field: 'properties.TGT_RATE',
          title: 'Rate ozs/ac',
          scale: { domain, range, type: 'linear' },
        },
      },
    };

    return (
      <Box key={stats.key}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
          }}
        >
          <h3>Created: {stats.stats.createdat}</h3>
          <h3>Total: {getRounded(stats.stats.total)}oz</h3>
          <h3>Min: {getRounded(stats.stats.min)}oz</h3>
          <h3>Max: {getRounded(stats.stats.max)}oz</h3>
          <h3>Avg: {getRounded(stats.stats.avg)}oz</h3>
        </div>
        <ApexChart
          xLabel="Rate (ozs/ac)"
          yLabel="Acres"
          noRates={true}
          stats={formattedStats}
          chartWidth="500px"
          mapComponent={
            <VegaLite spec={mapSpec} style={{ paddingTop: '20px' }} />
          }
        />

        {canAdjust && (
          <div>
            <TextField
              variant="standard"
              label="Full Field Adjustment"
              InputLabelProps={{
                shrink: true,
              }}
              value={(adjustments[stats.key] || {}).fullFieldAdjustment}
              onChange={(e) => {
                setAdjustments({
                  ...adjustments,
                  [stats.key]: {
                    ...adjustments[stats.key],
                    groupAdjustments: stats.baseDistribution.map((bd) => ({
                      minValue: bd.label,
                      maxValue: bd.label,
                      adjustmentFactor: e.target.value,
                    })),
                    fullFieldAdjustment: e.target.value,
                  },
                });
              }}
            />
            <GroupAdjustment
              aaManagedId={field.aaManagedId}
              groupBreaks={stats.baseDistribution.map((s) => parseInt(s.label))}
              currentValues={(adjustments[stats.key] || {}).groupAdjustments}
              enforceBounds={(af, minValue) =>
                enforceBounds(stats.key, stats.product, af, minValue)
              }
              onChange={(groupAdjustments) => {
                setAdjustments({
                  ...adjustments,
                  [stats.key]: {
                    ...adjustments[stats.key],
                    groupAdjustments,
                  },
                });
              }}
              onRevert={() => revertStats(stats.key)}
              onSave={saveStats}
              onClose={handleClose}
            />
          </div>
        )}
      </Box>
    );
  }

  function revertStats(key) {
    setAdjustments({
      ...adjustments,
      [key]: {
        ...adjustments[key],
        groupAdjustments: adjustments[key].groupAdjustments.map((ga) => ({
          ...ga,
          adjustmentFactor: 0,
        })),
        fullFieldAdjustment: 0,
      },
    });
  }

  function enforceBounds(prodTime, prodUtilized, originalAf, minValue) {
    const maxAdjustmentFactor = MAX_ADJUSTMENT_FACTOR[prodUtilized];
    const bound = Math.min(
      Math.max(
        parseInt(isNaN(originalAf) ? 0 : originalAf) +
          parseInt(isNaN(minValue) ? 0 : minValue),
        0,
      ) - parseInt(isNaN(minValue) ? 0 : minValue),
      BASE_RATE_MAPPING[prodUtilized][prodTime] * maxAdjustmentFactor -
        parseInt(isNaN(minValue) ? 0 : minValue),
    );

    return bound;
  }

  function saveStats() {
    const prodUtilized = allStats[currentTab].product;
    const prodTime = allStats[currentTab].key;
    const fixedAdjustments = adjustments[prodTime].groupAdjustments.map(
      (ga) => ({
        ...ga,
        adjustmentFactor: enforceBounds(
          prodTime,
          prodUtilized,
          ga.adjustmentFactor,
          ga.minValue,
        ),
      }),
    );

    return Promise.all([
      setNProtectantGroupAdjustments(
        field.aaManagedId,
        prodTime,
        prodUtilized,
        fixedAdjustments,
      ),
      setNProtectantFullFieldAdjustment(
        field.aaManagedId,
        prodTime,
        prodUtilized,
        adjustments[prodTime].fullFieldAdjustment,
      ),
    ]).then(() => {
      setAdjustments({
        ...adjustments,
        [prodTime]: {
          ...adjustments[prodTime],
          groupAdjustments: fixedAdjustments,
        },
      });
      reloadFields(true);
      refreshMap();
    });
  }

  function buildModalContent() {
    if (!tabs) return null;

    return (
      <>
        <Tabs value={currentTab} onChange={onTabChange}>
          {tabs.map(buildTabHeader)}
        </Tabs>
        {tabs.map(buildTabContent)}
      </>
    );
  }

  function rgbaToHex(rgba) {
    const extractionRegEx = /\(([^)]+)\)/;
    const numbers = extractionRegEx.exec(rgba);
    if (!numbers[0]) return '#777777'; // Default to return gray.
    const trimmedNumbers = numbers[0].substring(1, numbers[0].length - 1);
    const splits = trimmedNumbers.split(',').map((sp) => sp.replace(/ /g, ''));
    const numericSplits = splits.map((numStr) => parseInt(numStr, 10));
    const [r, g, b] = numericSplits;
    let rHex = r.toString(16);
    rHex = rHex.length === 1 ? `0${rHex}` : rHex;
    let gHex = g.toString(16);
    gHex = gHex.length === 1 ? `0${gHex}` : gHex;
    let bHex = b.toString(16);
    bHex = bHex.length === 1 ? `0${bHex}` : bHex;
    return `#${rHex}${gHex}${bHex}`;
  }
}
